import React, { useState, useEffect } from 'react';
import { FileCheck, FileText, X, Eye, CheckSquare, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import api from '../api';

const PersonalData = ({ onClose }) => {
  const [userRole, setUserRole] = useState(sessionStorage.getItem('userRole') || '');
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Show CV/CBU for CO, ACO, CD and PR
  const isStaff = ['CG', 'CO', 'ACO', 'CD', 'PR', 'CA', 'DO'].includes(userRole);

  const roleOptions = [
    { value: 'AL', name: 'Alumno' },
    { value: 'RM', name: 'Referente Municipal' },
    { value: 'ACO', name: 'Auxiliar de la Coordinadora Operativa' },
    { value: 'CO', name: 'Coordinador Operativo' },
    { value: 'CG', name: 'Coordinadora General' },
    { value: 'CA', name: 'Coordinadora Académica' }
  ];

  const [diplomaturasVigentes, setDiplomaturasVigentes] = useState([]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: sessionStorage.getItem('userEmail') || '',
    dni: '',
    age: '',
    highest_studies: '',
    biography: '',
    coordinated_diplos: [],
    teaching_subjects: []
  });
  const [files, setFiles] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [existingObservations, setExistingObservations] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('users/profile/').then(res => {
      // Load coordinated diplos: prefer API data, fallback to sessionStorage
      const apiCoordDiplos = res.data.coordinated_diplos
        ? res.data.coordinated_diplos.map(String)
        : [];
      const savedDiplos = apiCoordDiplos.length > 0
        ? apiCoordDiplos
        : (JSON.parse(sessionStorage.getItem('userCoordinatedDiplos')) || []);
      // Keep sessionStorage in sync for CalendarView filter
      if (apiCoordDiplos.length > 0) {
        sessionStorage.setItem('userCoordinatedDiplos', JSON.stringify(apiCoordDiplos));
      }

      // Normalize teaching_subjects to numbers to avoid type mismatch
      const teachingSubjects = (res.data.teaching_subjects || []).map(Number);

      setFormData({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        email: res.data.email || sessionStorage.getItem('userEmail') || '',
        dni: res.data.dni || '',
        age: res.data.age || '',
        highest_studies: res.data.highest_studies || '',
        biography: res.data.biography || '',
        coordinated_diplos: savedDiplos,
        teaching_subjects: teachingSubjects
      });
      // Role comes from the backend — user cannot change it
      if (res.data.role) {
        setUserRole(res.data.role);
      }
      
      // Map existing documents
      const docs = {};
      const obsvs = {};
      res.data.documents?.forEach(d => {
        if (d.doc_type === 'DNI_F') { docs.dni_frente = d.file; obsvs.dni_frente = d.observation; }
        if (d.doc_type === 'DNI_D') { docs.dni_dorso = d.file; obsvs.dni_dorso = d.observation; }
        if (d.doc_type === 'EST') { docs.estudios = d.file; obsvs.estudios = d.observation; }
        if (d.doc_type === 'CV') { docs.cv = d.file; obsvs.cv = d.observation; }
        if (d.doc_type === 'CBU') { docs.cbu = d.file; obsvs.cbu = d.observation; }
        if (d.doc_type === 'CERT_DIP') { docs.cert_diplo = d.file; obsvs.cert_diplo = d.observation; }
        if (d.doc_type === 'CERT_MAT') { docs.cert_materia = d.file; obsvs.cert_materia = d.observation; }
      });
      setExistingFiles(docs);
      setExistingObservations(obsvs);
      
      setLoading(false);
    }).catch(() => {
      setFormData(prev => ({
        ...prev,
        email: prev.email || sessionStorage.getItem('userEmail') || '',
      }));
      setLoading(false);
    });

    api.get('diplomas/').then(res => {
      setDiplomaturasVigentes(res.data);
    }).catch(err => console.error('Error fetching diplomaturas:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = new FormData();
    
    // Serialize form fields properly — arrays must be sent as multiple entries
    Object.keys(formData).forEach(key => {
        if (key === 'teaching_subjects' || key === 'coordinated_diplos') {
            formData[key].forEach(id => data.append(key, id));
        } else {
            data.append(key, formData[key]);
        }
    });

    if (files.dni_frente) data.append('dni_frente', files.dni_frente);
    if (files.dni_dorso) data.append('dni_dorso', files.dni_dorso);
    if (files.estudios) data.append('estudios', files.estudios);
    if (files.cv) data.append('cv', files.cv);
    if (files.cbu) data.append('cbu', files.cbu);
    if (files.cert_diplo) data.append('cert_diplo', files.cert_diplo);
    if (files.cert_materia) data.append('cert_materia', files.cert_materia);

    try {
      if (userRole !== sessionStorage.getItem('userRole')) {
        sessionStorage.setItem('userRole', userRole);
      }
      
      if (userRole === 'CD') {
        sessionStorage.setItem('userCoordinatedDiplos', JSON.stringify(formData.coordinated_diplos));
      } else {
        sessionStorage.removeItem('userCoordinatedDiplos');
      }
      
      await api.post('users/profile/', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage('Datos guardados correctamente');
      setTimeout(onClose, 1500);
    } catch (err) {
      setMessage('Error al guardar datos');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (itemKey) => {
    if (!window.confirm(`¿Está seguro de que desea eliminar este documento?`)) return;

    const keyMap = {
      'dni_frente': 'DNI_F',
      'dni_dorso': 'DNI_D',
      'estudios': 'EST',
      'cv': 'CV',
      'cbu': 'CBU',
      'cert_diplo': 'CERT_DIP',
      'cert_materia': 'CERT_MAT'
    };

    try {
      await api.post('users/delete_document/', { doc_type: keyMap[itemKey] });
      setExistingFiles(prev => {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      });
      setFiles(prev => {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      });
      setExistingObservations(prev => {
        const next = { ...prev };
        delete next[itemKey];
        return next;
      });
      setMessage('Documento eliminado');
    } catch (err) {
      console.error('Error deleting document:', err);
      setMessage('Error al eliminar el documento');
    }
  };

  if (loading) return null;

  return (
    <div className={`modal-overlay ${isExpanded ? 'expanded' : ''}`}>
      <div className={`modal-content glassmorphism p-5 animate-fade-in ${isExpanded ? 'modal-expanded' : ''}`}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="section-title mb-0">Datos Personales</h2>
          <div className="d-flex gap-2">
            <button 
              type="button" 
              onClick={() => setIsExpanded(!isExpanded)} 
              className="btn-glass p-1 d-flex align-items-center justify-content-center"
              title={isExpanded ? "Contraer" : "Ampliar"}
              style={{ width: '32px', height: '32px', borderRadius: '8px' }}
            >
              {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              type="button"
              className="btn-close-premium"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">NOMBRE</label>
              <input type="text" className="form-control" value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">APELLIDO</label>
              <input type="text" className="form-control" value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">DNI</label>
              <input type="text" className="form-control" value={formData.dni} onChange={e => setFormData({...formData, dni: e.target.value})} />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">EDAD</label>
              <input type="number" className="form-control" value={formData.age} onChange={e => setFormData({...formData, age: e.target.value})} />
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold text-muted">CORREO ELECTRÓNICO</label>
              <input type="email" className="form-control bg-light" value={formData.email} readOnly title="El correo no se puede cambiar desde aquí" />
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold text-muted">ESTUDIOS CURSADOS</label>
              <input type="text" className="form-control" value={formData.highest_studies} onChange={e => setFormData({...formData, highest_studies: e.target.value})} />
            </div>
            
            <div className="col-12 mt-2">
              <label className="form-label small fw-bold text-muted">ROL ASIGNADO</label>
              <input
                type="text"
                className="form-control bg-light text-muted"
                value={roleOptions.find(r => r.value === userRole)?.name || userRole || 'Sin rol asignado'}
                readOnly
                style={{ cursor: 'not-allowed' }}
                title="El rol es asignado por un administrador o coordinador"
              />
              <small className="text-muted">Este campo es gestionado por una autoridad competente.</small>
            </div>
            
            {userRole === 'CD' && (
              <div className="col-12 mt-3 animate-fade-in">
                <label className="form-label small fw-bold text-info mb-1">DIPLOMATURAS A CARGO</label>
                <small className="d-block text-muted mb-2">Asignadas por el Auxiliar CO.</small>
                <div className="d-flex flex-column gap-2 border rounded p-3 bg-light" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                  {diplomaturasVigentes.filter(d => formData.coordinated_diplos.includes(d.id.toString())).length > 0 ? (
                    diplomaturasVigentes
                      .filter(d => formData.coordinated_diplos.includes(d.id.toString()))
                      .map(d => (
                        <span key={d.id} className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-3 py-2 rounded d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
                          {d.name} <small className="opacity-75">({d.municipio})</small>
                        </span>
                      ))
                  ) : (
                    <span className="text-muted small text-center">Sin diplomaturas asignadas aún.</span>
                  )}
                </div>
              </div>
            )}

            {userRole === 'DO' && (
              <div className="col-12 mt-3 animate-fade-in">
                <label className="form-label small fw-bold text-primary mb-1">MIS DIPLOMATURAS Y MATERIAS</label>
                <small className="d-block text-muted mb-2">Asignadas por el Coordinador de Diplomatura.</small>
                <div className="d-flex flex-column gap-2 border rounded p-3 bg-light" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {diplomaturasVigentes.length > 0 ? (
                    diplomaturasVigentes.map(d => {
                      const assignedSubs = d.subjects?.filter(sub =>
                        formData.teaching_subjects.map(Number).includes(Number(sub.id))
                      ) || [];
                      if (assignedSubs.length === 0) return null;
                      return (
                        <div key={d.id} className="mb-1">
                          <div className="fw-bold small text-muted border-bottom mb-2 pb-1">{d.name} <small className="opacity-75">({d.municipio})</small></div>
                          <div className="d-flex flex-wrap gap-2">
                            {assignedSubs.map(sub => (
                              <span key={sub.id} className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 rounded" style={{ fontSize: '0.8rem' }}>
                                {sub.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  ) : null}
                  {formData.teaching_subjects.length === 0 && (
                    <span className="text-muted small text-center">Sin materias asignadas aún.</span>
                  )}
                </div>
              </div>
            )}
            
            <div className="col-12 mt-4">
              <label className="form-label small fw-bold text-primary">CARGA DE DOCUMENTOS (PDF)</label>
              <div className="row g-2">
                {[
                  { key: 'dni_frente', label: 'DNI Frente', show: true },
                  { key: 'dni_dorso', label: 'DNI Dorso', show: true },
                  { key: 'estudios', label: 'Cert. Estudios', show: true },
                  { key: 'cv', label: 'Currículum Vitae', show: isStaff },
                  { key: 'cbu', label: 'Constancia CBU', show: isStaff }
                ].filter(item => item.show).map(item => (
                  <div className="col-md-4" key={item.key}>
                    <div className="position-relative">
                      <label 
                        className={`file-upload-box ${(files[item.key] || existingFiles[item.key]) ? 'active' : ''} mb-0`}
                        style={{ width: '100%' }}
                      >
                        <div className="d-flex flex-column align-items-center">
                          {(files[item.key] || existingFiles[item.key]) ? (
                            <FileCheck size={24} className="text-success mb-2" />
                          ) : (
                            <FileText size={24} className="text-muted mb-2" />
                          )}
                          <span className="small fw-bold">{item.label}</span>
                          {files[item.key] && (
                            <span className="text-primary x-small mt-1 text-truncate w-100" style={{fontSize: '0.7rem'}}>
                              {files[item.key].name}
                            </span>
                          )}
                          {(!files[item.key] && existingFiles[item.key]) && (
                            <span className="text-success x-small mt-1" style={{fontSize: '0.7rem'}}>
                              Cargado ✓
                            </span>
                          )}
                        </div>
                        <input 
                          type="file" 
                          accept="application/pdf" 
                          style={{ display: 'none' }}
                          onChange={e => setFiles({...files, [item.key]: e.target.files[0]})} 
                        />
                      </label>
                      {existingFiles[item.key] && (
                        <div className="position-absolute top-0 end-0 m-1 d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-glass p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(existingFiles[item.key], '_blank');
                            }}
                            title="Ver PDF"
                            style={{ borderRadius: '50%' }}
                          >
                            <Eye size={14} className="text-primary" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-glass p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteDocument(item.key);
                            }}
                            title="Eliminar"
                            style={{ borderRadius: '50%' }}
                          >
                            <Trash2 size={14} className="text-danger" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {message && <div className={`alert mt-3 ${message.includes('Error') ? 'alert-danger' : 'alert-success'}`}>{message}</div>}
          <div className="mt-5 d-flex gap-2">
            <button type="submit" disabled={saving} className="btn btn-primary-gradient flex-grow-1 py-3 font-bold">
              {saving ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-glass px-4">CANCELAR</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalData;
